import { type ReactNode } from 'react';
import { useFormField, type useFormFieldResult } from '../../useFormField';
import type { EnumerationViewModel } from '@generated';
import { RadioGroupField, type RadioGroupFieldProps } from '../base-fields/RadioGroupField';
import type { StripControlledProperties } from '../../types';
import { Radio } from '../base-controls/Radio';
import { cn } from '~/shared/components/utils';

type RadioButtonOption = string | EnumerationViewModel | { id: string | number; name: string };
export interface RadioGroupFormFieldProps extends StripControlledProperties<RadioGroupFieldProps> {
	name: string;
	options?: RadioButtonOption[];
	getOptions?: (fieldProps: useFormFieldResult<string | null>) => ReactNode;
	canBeCleared?: boolean;
}

export function RadioGroupFormField(props: RadioGroupFormFieldProps) {
	const fieldProps = useFormField<string | null>({ ...props, validateOnChange: true });

	const options =
		props.options && props.options.length > 0
			? props.options.map(option => {
					if (typeof option === 'string') {
						return <Radio key={option} value={option} label={option} size={props.size} />;
					} else if ('id' in option && 'name' in option) {
						const optionId = option.id.toString();
						return <Radio key={optionId} value={optionId} label={option.name} size={props.size} />;
					}
					const optionId = option.id.toString();
					return <Radio key={optionId} value={optionId} label={option.label} size={props.size} />;
			  })
			: props.getOptions
			? props.getOptions(fieldProps)
			: null;

	return (
		<RadioGroupField
			{...props}
			{...fieldProps}
			value={fieldProps.value ? fieldProps.value.toString() : null}
			orientation={props.orientation ?? 'vertical'}
		>
			{options}
			{props.canBeCleared && fieldProps.value && !fieldProps.isReadOnly && (
				<button
					type="button"
					className={cn('flex text-xs text-blue-600 hover:text-blue-800', props.orientation !== 'horizontal' && 'mt-2')}
					onClick={() => fieldProps.onReset()}
				>
					Reset
				</button>
			)}
		</RadioGroupField>
	);
}
